import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { pathByRouteName } from '../../../util/routes';
import {
  CUSTOMER_DASHBOARD_MAIN_NAV_ITEMS,
  CUSTOMER_SETTINGS_NAV_PARENT,
  CustomerNavIconLogout,
} from '../../../util/customerNavItems';

import NamedLink from '../../../components/NamedLink/NamedLink';
import NotificationBadge from '../../../components/NotificationBadge/NotificationBadge';

import LayoutWrapperAccountSettingsSideNav from './LayoutWrapperAccountSettingsSideNav';

import css from './LayoutSideNavigation.module.css';

const ACCOUNT_SETTINGS_PAGES = new Set([
  'ProfileSettingsPage',
  'ContactDetailsPage',
  'PasswordChangePage',
  'StripePayoutPage',
  'PaymentMethodsPage',
  'ManageAccountPage',
]);

const ChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6 9l6 6 6-6"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Customer hub sidebar: overview, orders, and Settings with account sub-links.
 */
const LayoutCustomerSideNav = ({ onLogout, accountSettingsNavProps }) => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();
  const orderNotificationsCount = useSelector(
    state => state.user?.currentUserOrderNotificationCount ?? 0
  );

  const { currentPage } = accountSettingsNavProps;
  const isAccountSection = ACCOUNT_SETTINGS_PAGES.has(currentPage);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(isAccountSection);

  useEffect(() => {
    if (!isAccountSection) {
      setSettingsMenuOpen(false);
    }
  }, [currentPage, isAccountSection]);

  const settingsExpanded = isAccountSection || settingsMenuOpen;
  const toggleSettingsMenu = () => setSettingsMenuOpen(v => !v);

  const overviewPath = pathByRouteName('CustomerDashboardPage', routeConfiguration);
  const isPathActive = (routeName, params = {}) => {
    const p = pathByRouteName(routeName, routeConfiguration, params);
    return location.pathname === p;
  };

  const inboxNotificationCountForItem = ({ routeName, params }) => {
    if (routeName === 'InboxPage' && params?.tab === 'orders') {
      return orderNotificationsCount;
    }
    return 0;
  };

  const renderMainLink = item => {
    const { routeName, params, messageId, Icon } = item;
    const pathMatches = isPathActive(routeName, params);
    const active =
      routeName === 'CustomerDashboardPage'
        ? location.pathname === overviewPath
        : pathMatches;

    const notificationCount = inboxNotificationCountForItem(item);

    return (
      <NamedLink
        key={messageId}
        name={routeName}
        params={params}
        className={classNames(css.sellerNavLink, { [css.sellerNavLinkActive]: active })}
      >
        <span className={css.sellerNavIcon}>
          <Icon />
        </span>
        <span className={css.sellerNavLinkLabel}>
          <FormattedMessage id={messageId} />
        </span>
        {notificationCount > 0 ? (
          <NotificationBadge className={css.sellerNavNotificationBadge} count={notificationCount} />
        ) : null}
      </NamedLink>
    );
  };

  return (
    <div className={css.sellerSideNav}>
      <nav
        className={css.sellerSideNavMain}
        aria-label={intl.formatMessage({ id: 'CustomerDashboard.sidebarAria' })}
      >
        {CUSTOMER_DASHBOARD_MAIN_NAV_ITEMS.map(item => renderMainLink(item))}

        <button
          type="button"
          id="customer-hub-settings-trigger"
          className={classNames(css.sellerNavLink, css.sellerNavSettingsTrigger, {
            [css.sellerNavLinkActive]: isAccountSection,
          })}
          aria-expanded={settingsExpanded}
          aria-controls="customer-hub-settings-submenu"
          onClick={toggleSettingsMenu}
        >
          <span className={css.sellerNavIcon}>
            <CUSTOMER_SETTINGS_NAV_PARENT.Icon />
          </span>
          <FormattedMessage id={CUSTOMER_SETTINGS_NAV_PARENT.messageId} />
          <span
            className={classNames(css.sellerNavChevron, {
              [css.sellerNavChevronOpen]: settingsExpanded,
            })}
          >
            <ChevronDown />
          </span>
        </button>
        {settingsExpanded ? (
          <div id="customer-hub-settings-submenu" className={css.sellerNavSubmenu}>
            <LayoutWrapperAccountSettingsSideNav
              accountSettingsNavProps={accountSettingsNavProps}
              ariaLabel={intl.formatMessage({
                id: 'LayoutSideNavigation.screenreader.accountNavigation',
              })}
              variant="embedded"
            />
          </div>
        ) : null}
      </nav>

      <div className={css.sellerSideNavFooter}>
        <button
          type="button"
          className={css.sellerNavLogoutBtn}
          onClick={() => onLogout(history.push)}
        >
          <span className={css.sellerNavIcon}>
            <CustomerNavIconLogout />
          </span>
          <FormattedMessage id="CustomerDashboard.logout" />
        </button>
      </div>
    </div>
  );
};

LayoutCustomerSideNav.propTypes = {
  onLogout: PropTypes.func.isRequired,
  accountSettingsNavProps: PropTypes.shape({
    currentPage: PropTypes.string.isRequired,
    showPaymentMethods: PropTypes.bool,
    showPayoutDetails: PropTypes.bool,
  }).isRequired,
};

export default LayoutCustomerSideNav;
